// src/services/activities.ts
import { apiJson, apiRequest } from './http';

export type ActivityTypeCode = string;

export type ActivityTypeDTO = {
  id: number;
  code: string;
  label: string;
  icon?: string | null;
  active: boolean;

  // allineato al backend
  defaultDurationMinutes?: number | null;
  defaultCost?: number | null;
};

export type ActivityAttachmentInput = {
  filename: string;
  url?: string | null;
  contentType?: string | null;
  sizeBytes?: number | null;
};

export type AppointmentInput = {
  title: string;
  description?: string;
  location?: string;
  startAt: string;   // ISO
  endAt?: string;    // ISO
  allDay?: boolean;
  confirmed?: boolean;
  cancelled?: boolean;
  videomakerId?: number | null;
};

export type AppointmentDTO = {
  id: number;
  title: string;
  description?: string | null;
  location?: string | null;
  startAt: string;
  endAt?: string | null;
  allDay: boolean;
  confirmed: boolean;
  cancelled: boolean;
  videomakerId?: number | null;
  videomakerName?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type CreateActivityBase = {
  description?: string;
  customerId: number;
  occurredAt?: string;  // ISO
  visibility?: boolean;
  attachments?: ActivityAttachmentInput[];
};

// name obbligatorio SOLO per EXTRA, opzionale per gli altri
export type CreateActivityRequest =
  | (CreateActivityBase & {
      type: Exclude<ActivityTypeCode, 'EXTRA' | 'MEDIA_SHOOT'>;
      name?: string;
    })
  | (CreateActivityBase & {
      type: 'MEDIA_SHOOT';
      name?: string;
      appointment: AppointmentInput;
    })
  | (CreateActivityBase & {
      type: 'EXTRA';
      name: string;
      // ▼ override SOLO per EXTRA
      durationMinutes?: number | null;
      cost?: number | null;
    });

export type ActivityDTO = {
  id: number;
  name: string;
  description?: string | null;
  customerId: number | null;
  customerName?: string | null;
  userId: number | null;

  // type valorizzato lato BE in activityType (e opzionalmente code in "type")
  activityType?: ActivityTypeDTO | null;
  type?: ActivityTypeCode;

  userFirstName?: string | null;
  userLastName?: string | null;
  userEmail?: string | null;
  userRole?: string | null;
  occurredAt: string;  // ISO
  visibility: boolean;

  attachments: {
    id: number;
    filename: string;
    url?: string | null;
    contentType?: string | null;
    sizeBytes?: number | null;
    uploadedAt?: string | null;
    uploadedById?: number | null;
  }[];

  appointment?: AppointmentDTO | null;

  // ▼ Valori EFFETTIVI ritornati dal BE (override se EXTRA, altrimenti default del tipo)
  durationMinutes?: number | null;
  cost?: number | null;
};

export type Page<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number; // page index
  size: number;   // page size
};

/* --------------- CRUD attività --------------- */

export async function createActivity(payload: CreateActivityRequest): Promise<ActivityDTO> {
  return apiJson<ActivityDTO>('/p/activities', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function listActivitiesByCustomer(
  customerId: number,
  page = 0,
  size = 20
): Promise<Page<ActivityDTO>> {
  const params = new URLSearchParams({
    customerId: String(customerId),
    page: String(page),
    size: String(size),
  });
  return apiJson<Page<ActivityDTO>>(`/p/activities?${params.toString()}`);
}

export async function getActivity(id: number): Promise<ActivityDTO> {
  return apiJson<ActivityDTO>(`/p/activities/${id}`);
}

export async function deleteActivity(id: number): Promise<void> {
  await apiJson<void>(`/p/activities/${id}`, { method: 'DELETE' });
}


export type ActivityListParams = {
  page?: number;
  size?: number;
  sort?: string;
  customerId?: number;
  userId?: number;
  from?: string; // ISO
  to?: string;   // ISO
  type?: ActivityTypeCode;
};

const inFlightRequests = new Map<string, Promise<any>>();

function dedupeRequest<T>(key: string, run: () => Promise<T>): Promise<T> {
  const existing = inFlightRequests.get(key);
  if (existing) return existing as Promise<T>;
  const p = run().finally(() => {
    inFlightRequests.delete(key);
  });
  inFlightRequests.set(key, p);
  return p;
}

function buildActivitiesQuery(params?: ActivityListParams) {
  const qs = new URLSearchParams();
  if (params?.customerId != null) qs.set('customerId', String(params.customerId));
  if (params?.userId != null) qs.set('userId', String(params.userId));
  if (params?.from) qs.set('from', params.from);
  if (params?.to) qs.set('to', params.to);
  if (params?.type) qs.set('type', params.type);
  qs.set('page', String(params?.page ?? 0));
  qs.set('size', String(params?.size ?? 1000));
  if (params?.sort) qs.set('sort', params.sort);
  return qs;
}

export async function listActivities(params?: ActivityListParams) {
  const qs = buildActivitiesQuery(params);
  const key = `/p/activities?${qs.toString()}`;
  return dedupeRequest(key, () => apiJson<Page<ActivityDTO>>(key));
}

// --- LIST "all pages": scarica tutto evitando N+1 ---
export async function listAllActivities(params?: Omit<ActivityListParams, 'page'> & { maxPages?: number }) {
  const size = params?.size ?? 1000;
  const sort = params?.sort;
  const maxPages = params?.maxPages ?? 50; // safety cap
  let page = 0;
  let totalPages = 1;
  const acc: ActivityDTO[] = [];

  while (page < totalPages && page < maxPages) {
    const p = await listActivities({ ...params, page, size, sort });
    acc.push(...(p.content ?? []));
    totalPages = p.totalPages ?? 1;
    page++;
  }
  return acc;
}

export async function getActivitiesCount(
  params?: Omit<ActivityListParams, 'page' | 'size' | 'sort'>,
  options?: { useCountEndpoint?: boolean }
) {
  if (options?.useCountEndpoint) {
    const qs = new URLSearchParams();
    if (params?.customerId != null) qs.set('customerId', String(params.customerId));
    if (params?.userId != null) qs.set('userId', String(params.userId));
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.type) qs.set('type', params.type);

    try {
      const q = qs.toString();
      const res = await apiJson<any>(q ? `/p/activities/count?${q}` : '/p/activities/count');
      if (typeof res === 'number') return res;
      if (res && typeof res.count === 'number') return res.count;
      if (res && typeof res.total === 'number') return res.total;
    } catch {
      // fallback below
    }
  }

  const page = await listActivities({ ...params, page: 0, size: 1 });
  return page.totalElements ?? page.content?.length ?? 0;
}

/* --------------- FILES per Activity --------------- */

export type ActivityFileDTO = {
  id: number;
  filename: string;
  url?: string | null;
  contentType?: string | null;
  sizeBytes?: number | null;
  uploadedAt?: string | null;
  uploadedById?: number | null;
};

export async function listActivityFiles(
  activityId: number,
  page = 0,
  size = 10
): Promise<Page<ActivityFileDTO>> {
  return apiJson<Page<ActivityFileDTO>>(
    `/p/activities/${activityId}/files?page=${page}&size=${size}`
  );
}

export async function uploadActivityFiles(
  activityId: number,
  files: File[],
  note?: string | null
): Promise<ActivityFileDTO[]> {
  const form = new FormData();
  files.forEach(f => form.append('files', f));
  if (note) form.append('note', note);

  const res = await apiRequest(`/p/activities/${activityId}/files`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => '');
    throw new Error(msg || `Errore upload file (${res.status})`);
  }
  return res.json();
}

export async function deleteActivityFile(activityId: number, fileId: number): Promise<void> {
  await apiJson<void>(`/p/activities/${activityId}/files/${fileId}`, { method: 'DELETE' });
}

export async function downloadActivityFileBlob(activityId: number, fileId: number): Promise<Blob> {
  const res = await apiRequest(`/p/activities/${activityId}/files/${fileId}/download`, {
    method: 'GET',
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(txt || `Errore download (${res.status})`);
  }
  return res.blob();
}

// update attività
export async function updateActivity(
  id: number,
  payload: Partial<CreateActivityRequest>
): Promise<ActivityDTO> {
  return apiJson<ActivityDTO>(`/p/activities/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

/**
 * Aggiorna (merge) l'appointment di un'attività MEDIA_SHOOT.
 * NOTA: il backend richiede SEMPRE startAt quando si invia appointment nel PUT.
 */
export async function updateAppointment(
  activityId: number,
  patch: Partial<AppointmentInput>
): Promise<ActivityDTO> {
  const act = await getActivity(activityId);
  const appt = act.appointment;
  if (!appt) {
    throw new Error('Nessun appointment associato all’attività');
    }
  return updateActivity(activityId, {
    type: 'MEDIA_SHOOT',
    appointment: {
      startAt: patch.startAt ?? appt.startAt,
      endAt: patch.endAt ?? appt.endAt ?? undefined,
      title: patch.title ?? appt.title,
      description: patch.description ?? appt.description ?? undefined,
      location: patch.location ?? appt.location ?? undefined,
      allDay: patch.allDay ?? appt.allDay,
      confirmed: patch.confirmed ?? appt.confirmed,
      cancelled: patch.cancelled ?? appt.cancelled,
      videomakerId: patch.videomakerId ?? appt.videomakerId ?? null,
    },
  });
}

export async function setAppointmentConfirmed(
  activityId: number,
  confirmed: boolean
): Promise<ActivityDTO> {
  return updateAppointment(activityId, { confirmed });
}


export async function listActivitiesBulk(params: {
  customerIds?: number[];
  from?: string; // ISO
  to?: string;   // ISO
  page?: number;
  size?: number;
  sort?: string; // es: "occurredAt,desc"
}) {
  const qs = new URLSearchParams();
  if (params.customerIds?.length) {
    params.customerIds.forEach(id => qs.append('customerIds', String(id)));
  }
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  qs.set('page', String(params.page ?? 0));
  qs.set('size', String(params.size ?? 500));
  if (params.sort) qs.set('sort', params.sort);

  const res = await fetch(`/api/p/activities/bulk?${qs.toString()}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Errore caricamento attività (bulk)');
  return res.json(); // Page<ActivityDTO>
}
